import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { deleteRequest } from '../../requestUtils';
import { interviewAvailabilityShape } from '../../dateUtils';
import { analyticsTrack } from '../../analyticsUtils';
import InterviewAttendees from '../common/InterviewSubComponents/InterviewAttendees';
import InterviewTimeRange from '../common/InterviewSubComponents/InterviewTimeRange';
import InterviewTimezone from '../common/InterviewSubComponents/InterviewTimezone';
import InterviewMeetingDetails from '../common/InterviewSubComponents/InterviewMeetingDetails';
import InterviewActions from '../common/InterviewSubComponents/InterviewActions';
import './InterviewPanel.scss';

const interviewHeaderOpts = {
  timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
};
const availabilityOpts = {
  timeZone: 'UTC', weekday: 'long', month: 'short', day: 'numeric',
};

const MIN_AVAILABILITY_LEN = 1;

// This component is not very aptly named - used on Interview#show page
// Meanwhile, the Interview.jsx component would probably be better called
// InterviewPanel or InterviewBadge.
class InterviewPanel extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isViewingMore: false,
      showCancellationCopy: false,
      interview: this.props.interview,
    };

    this.handleInterviewMeetingDetailsSubmit = this.handleInterviewMeetingDetailsSubmit.bind(this);
    this.backToMyInterviewsPath = `${this.props.myInterviewsPath}?type=${this.props.interview.type}`;
    this.location = 'Interview Page View';
  }

  componentDidMount() {
    analyticsTrack('Interview Page Viewed', {});
  }

  getAvailabilities() {
    const { isViewingMore, interview } = this.state;

    if (isViewingMore) {
      return interview.availabilities;
    }

    return interview.availabilities.slice(0, MIN_AVAILABILITY_LEN); // only show the first
  }

  handleCancelInterview() {
    const {
      cancelInterviewPath, csrfToken, interviewResourceId, interview,
    } = this.props;
    const analyticsMessage = `Interview cancelled; type: ${interview.type}`;

    deleteRequest(cancelInterviewPath, csrfToken).then(() => {
      analyticsTrack(analyticsMessage, { location: this.location });
      const newUrl = `${this.backToMyInterviewsPath}&cancelled_interview=${interviewResourceId}`;
      window.location.href = newUrl;
    });
  }

  handleInterviewMeetingDetailsSubmit(response) {
    this.setState({ interview: response.data });
  }

  renderInterviewHeader() {
    const { projectName, projectUrl } = this.props;
    const { interview } = this.state;

    if (interview.accepted_time) {
      return (
        <div className="interview-panel-header mt-3">
          <InterviewTimeRange
            interviewTime={interview.accepted_time}
            opts={interviewHeaderOpts}
            containerClassName="d-inline-block body-text-1 medium-weight"
          />
          <div className="d-inline-block ms-2 body-text-3 muted-gray-text">
            <InterviewTimezone
              timezoneStr={interview.timezone}
              dateStr={interview.accepted_time.date}
            />
          </div>
          <div className="mt-3">
            <div className="body-text-1 medium-weight interview-candidate-and-round mb-4">
              Interview with
              {' '}
              {interview.attendees.consultant}
              {' '}
              (Round
              {' '}
              {interview.round_number}
              )
            </div>
            <a href={projectUrl} className="body-text-2 normal-weight project-name">
              {projectName}
            </a>
          </div>
        </div>
      );
    }

    return (
      <div className="interview-panel-header mt-3">
        <div className="body-text-1 medium-weight interview-candidate-and-round mb-4">
          {`Interview pending ${interview.type === 'pending' ? ' confirmation' : ' reschedule'}`}
        </div>
        <a href={projectUrl} className="body-text-2 normal-weight project-name">
          {projectName}
        </a>
      </div>
    );
  }

  renderViewMore(className, viewMoreCta = 'View More', viewLessCta = 'View Less') {
    const cta = this.state.isViewingMore ? viewLessCta : viewMoreCta;

    return (
      <a
        onClick={() => this.setState({ isViewingMore: !this.state.isViewingMore })}
        role="button"
        tabIndex="0"
        className={`view-more-cta ${className}`}
      >
        {cta}
      </a>
    );
  }

  renderUpcomingInterviewSubDivider() {
    const { editInterviewPath, isRep, csrfToken } = this.props;
    const { interview, isViewingMore } = this.state;

    const meetingDetails = interview.meeting_details;

    if (meetingDetails) {
      return (
        <div className="upcoming-interview-sub-divider-section">
          {this.renderViewMore('body-text-2 medium-weight mb-2', 'Show meeting details', 'Hide meeting details')}
          {isViewingMore
            && (
            <InterviewMeetingDetails
              isRep={isRep}
              meetingDetails={meetingDetails}
              csrfToken={csrfToken}
              editInterviewPath={editInterviewPath}
              onSubmit={this.handleInterviewMeetingDetailsSubmit}
            />
            )}
        </div>
      );
    }

    return (
      <div className="upcoming-interview-sub-divider-section">
        <InterviewMeetingDetails
          isRep={isRep}
          meetingDetails={meetingDetails}
          csrfToken={csrfToken}
          editInterviewPath={editInterviewPath}
          onSubmit={this.handleInterviewMeetingDetailsSubmit}
        />
      </div>
    );
  }

  renderPendingInterviewSubDivider() {
    const { interview } = this.state;

    const availabilitiesToRender = this.getAvailabilities();

    return (
      <div className="pending-interview-sub-divider-section">
        <div className="body-text-2 normal-weight mb-4">
          {interview.attendees.consultant.split(' ')[0]}
          {' '}
          did not select a time yet. All guests will be notified when a time is selected.
        </div>
        <div className="body-text-2 medium-weight mb-8">Your availability for this interview:</div>
        <div className="body-text-2 muted-gray-text mb-8">
          <InterviewTimezone
            timezoneStr={interview.timezone}
          />
        </div>
        <div className="interview-panel-availabilities body-text-2">
          {
            availabilitiesToRender.map((availability, i) => (
              <div
                className="availability-range-container"
                key={`${availability.start_time}${availability.date}`}
              >
                <InterviewTimeRange
                  interviewTime={availability}
                  opts={availabilityOpts}
                  containerClassName="interview-availability-item normal-weight mb-4"
                />
                {(i === availabilitiesToRender.length - 1) && this.renderViewMore('ms-3 medium-weight')}
              </div>
            ))
          }
        </div>
      </div>
    );
  }

  renderInterviewSubDivider() {
    const { interview } = this.state;

    if (interview.type === 'past') {
      return null;
    }

    return (
      <Fragment>
        <hr className="width-180" />
        {interview.type === 'upcoming' && this.renderUpcomingInterviewSubDivider()}
        {interview.type === 'pending' && this.renderPendingInterviewSubDivider()}
      </Fragment>
    );
  }

  renderInterviewPanelActions() {
    const { interview } = this.state;
    const {
      editInterviewPath,
      csrfToken,
      possibleInterviewers,
      invitableRoles,
      canEdit,
      canCancel,
    } = this.props;

    if (interview.type === 'past') { // nothing to render for past interviews
      return null;
    }

    if (!canEdit && !canCancel) { // if no action can be taken -
      return null;
    }

    return (
      <Fragment>
        <hr className="actions-divider" />
        <div className="action-container g-row--always g-row-justify-right">
          <InterviewActions
            editInterviewPath={editInterviewPath}
            csrfToken={csrfToken}
            possibleInterviewers={possibleInterviewers}
            invitableRoles={invitableRoles}
            interview={interview}
            interviewType={interview.type}
            location={this.location}
            canEdit={canEdit}
            canCancel={canCancel}
            onCancel={() => this.handleCancelInterview()}
          />
        </div>
      </Fragment>
    );
  }

  renderBackButton() {
    return (
      <a
        href={this.backToMyInterviewsPath}
        role="button"
        tabIndex="0"
        className="body-text-1 medium-weight"
      >
        <i className="fas fa-angle-left me-3" />
        Back to My Interviews
      </a>
    );
  }

  render() {
    const { interview } = this.state;

    return (
      <div className="style-guide card mb-0 border-0">
        <div className="interview-info-card">
          {this.renderBackButton()}
          {this.renderInterviewHeader()}
          <InterviewAttendees
            attendees={interview.attendees}
            horizontal
          />
          {this.renderInterviewSubDivider()}
        </div>
        {this.renderInterviewPanelActions()}
      </div>
    );
  }
}

InterviewPanel.propTypes = {
  interview: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string,
    round_number: PropTypes.number,
    accepted_time: interviewAvailabilityShape,
    availabilities: PropTypes.arrayOf(interviewAvailabilityShape),
    invited_emails: PropTypes.arrayOf(PropTypes.string),
    timezone: PropTypes.string,
  }).isRequired,
  myInterviewsPath: PropTypes.string.isRequired,
  cancelInterviewPath: PropTypes.string.isRequired,
  editInterviewPath: PropTypes.string.isRequired,
  projectName: PropTypes.string.isRequired,
  projectUrl: PropTypes.string.isRequired,
  possibleInterviewers: PropTypes.shape({
    current_user_permission: PropTypes.shape({}),
    team_members: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  invitableRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  csrfToken: PropTypes.string.isRequired,
  isRep: PropTypes.bool,
  interviewResourceId: PropTypes.string.isRequired,
  canEdit: PropTypes.bool,
  canCancel: PropTypes.bool,
};

InterviewPanel.defaultProps = {
  isRep: false,
  canEdit: false,
  canCancel: false,
};

export default InterviewPanel;
